/**
 * Página de Dashboard
 * Página protegida com layout customizado por role
 * Carrega dados reais do backend
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getAuthToken } from '@/utils/auth';
import { hasPersistedAuthSession } from '@/lib/postAuthSession';
import {
  isAdminRole,
  isDoctorRole,
  isPatientRole,
  shouldFetchAdminResources,
} from '@/lib/roleRouting';
import { Sidebar } from '@/components/common/Sidebar';
import {
  DashboardHeader,
  DashboardStatCard,
  ConsultationGrowthChart,
  RecentApplicationsTable,
  SystemAlertsPanel,
  PerformanceLog,
  PatientNextAppointmentCard,
  NextAppointmentsTable,
  ActivityTimeline,
  DoctorDocumentsUpload,
  ProfessionalDocumentsUpload,
  MedicalCredentialsUpload,
  AvailabilityToggle,
  OnDemandMatchListener,
  ConnectionsList,
} from '@/components/dashboard';
import { DoctorRejectionNotice } from '@/components/dashboard/DoctorRejectionNotice';
import { formatDoctorSatisfactionDisplay } from '@/lib/doctorSatisfaction';
import {
  getUpcomingAppointments,
  isAppointmentToday,
} from '@/lib/doctorUpcomingAppointments';

import {
  AlertCircle,
  Users,
  DollarSign,
  Stethoscope,
  Heart,
  Calendar,
  FileText,
  Loader2,
  ChevronRight,
  FlaskConical,
  ClipboardList,
} from 'lucide-react';
import { 
  getDoctorByUserId,
  getDoctorAnalytics,
  getAdminStats,
  getPatientByUserId,
  getPendingApprovals,
  getAnalyticsAppointments
} from '@/services/index';
import { notificationsService } from '@/services/notifications';
import { getMyDoctorAppointments } from '@/services/appointments';
import { getPatientDocuments } from '@/services/documents';
import type { DocumentDTO } from '@/services/documents';
import { patientDocumentDisplayName } from '@/lib/clinicalDocuments';
import type { AppointmentDTO } from '@/services/appointments';
import type {
  DoctorDTO,
  AdminStats
} from '@/services/index';

interface DashboardStats extends Partial<AdminStats['counts']> {
  patientsCount?: number; // New field for scoped doctor patient count
  satisfactionRating?: number | null; // média real (null se sem avaliações)
  ratingsCount?: number;
  appointments?: AdminStats['appointments'];
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({});
  const [adminAppointmentsEvolution, setAdminAppointmentsEvolution] = useState<any[]>([]);
  const [adminPendingDoctors, setAdminPendingDoctors] = useState<any[]>([]);
  const [adminAlerts, setAdminAlerts] = useState<any[]>([]);
  const [recentDocuments, setRecentDocuments] = useState<DocumentDTO[]>([]);
  const [doctorAppointments, setDoctorAppointments] = useState<AppointmentDTO[]>([]);
  const [doctorProfile, setDoctorProfile] = useState<DoctorDTO | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      // Evita bounce para login quando token/user já estão no storage e o Context ainda não hidratou
      if (getAuthToken() || hasPersistedAuthSession()) return;
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Load dashboard data based on user role — nunca chamar /admin/* sem role ADMIN
  useEffect(() => {
    if (!user || isLoading) return;

    const loadData = async () => {
      setIsLoadingData(true);
      setError(null);

      try {
        if (shouldFetchAdminResources(user.role, isLoading)) {
          const [statsResponse, appsResponse, pendingResponse, alertsResponse] = await Promise.all([
            getAdminStats(),
            getAnalyticsAppointments(),
            getPendingApprovals(),
            notificationsService.getAll().catch(() => [])
          ]);

          if (statsResponse.success && statsResponse.data) {
            const { counts, appointments } = statsResponse.data;
            setStats({
              doctors: counts.doctors,
              patients: counts.patients,
              admins: counts.admins,
              pendingApprovals: counts.pendingApprovals,
              appointments,
            });
          } else {
            if (statsResponse.message === 'Sessão expirada') return;
            if (statsResponse.message === 'Acesso negado') {
              console.warn(
                '[Dashboard] FORBIDDEN esperado por role incorreta ao buscar admin stats. Role:',
                user.role,
              );
              return;
            }
            throw new Error(statsResponse.error || 'Falha ao carregar estatísticas');
          }

          if (appsResponse.success && appsResponse.data?.evolutionByType) {
            setAdminAppointmentsEvolution(appsResponse.data.evolutionByType);
          }
          if (pendingResponse.success && pendingResponse.data) {
            const mappedDoctors = pendingResponse.data.map(doc => ({
              id: doc.id,
              name: doc.user?.name || doc.crm,
              specialty: doc.specialty || 'Não informada',
              appliedDate: new Date(doc.createdAt).toLocaleDateString('pt-BR'),
              licenseId: doc.crm || 'N/A',
              status: doc.status === 'APPROVED' ? 'approved' : doc.status === 'REJECTED' ? 'rejected' : 'pending'
            }));
            setAdminPendingDoctors(mappedDoctors as any[]);
          }
          
          if (Array.isArray(alertsResponse)) {
            const mappedAlerts = alertsResponse.map(a => {
              // Convert error to urgent for frontend
              const frontendType = a.type === 'error' ? 'urgent' : a.type;
              
              // Calculate time ago
              const date = new Date(a.createdAt);
              const now = new Date();
              const diffMs = now.getTime() - date.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              const diffHours = Math.floor(diffMins / 60);
              const diffDays = Math.floor(diffHours / 24);
              
              let timeAgo = '';
              if (diffDays > 0) timeAgo = `${diffDays}d atrás`;
              else if (diffHours > 0) timeAgo = `${diffHours}h atrás`;
              else timeAgo = `${diffMins}m atrás`;

              return {
                id: a.id,
                type: frontendType,
                title: a.title,
                description: a.description,
                timeAgo: timeAgo,
                action: a.link ? 'Ver Detalhes' : undefined
              };
            });
            setAdminAlerts(mappedAlerts);
          }

        } else if (isDoctorRole(user.role)) {
          const startOfToday = new Date();
          startOfToday.setHours(0, 0, 0, 0);

          // Perfil + próximas consultas em paralelo.
          // Consultas: endpoint autenticado (mesmo vínculo da Agenda — users.id).
          const [doctorResponse, appointmentsResponse] = await Promise.all([
            getDoctorByUserId(user.id),
            getMyDoctorAppointments(startOfToday.toISOString()),
          ]);

          if (doctorResponse.success && doctorResponse.data) {
            setDoctorProfile(doctorResponse.data);
            const doctorId = doctorResponse.data.id;

            const analyticsResponse = await getDoctorAnalytics(doctorId);
            if (analyticsResponse.success && analyticsResponse.data) {
              setStats({
                patientsCount: analyticsResponse.data.patientsCount,
                satisfactionRating: analyticsResponse.data.satisfactionRating,
                ratingsCount: analyticsResponse.data.ratingsCount ?? 0,
              });
            }
          }

          if (appointmentsResponse.success === false) {
            if (appointmentsResponse.message === 'Sessão expirada') return;
            throw new Error(
              appointmentsResponse.error || 'Falha ao carregar próximas consultas',
            );
          }

          const appointmentsData = Array.isArray(appointmentsResponse.data)
            ? appointmentsResponse.data
            : [];
          setDoctorAppointments(appointmentsData);
        } else if (isPatientRole(user.role)) {
          const patientResponse = await getPatientByUserId(user.id);
          if (patientResponse.success && patientResponse.data) {
            const patientUserId =
              patientResponse.data.userId || patientResponse.data.id;
            const docsResponse = await getPatientDocuments(patientUserId, 3);
            if (docsResponse.success) {
              const documentsData = Array.isArray(docsResponse.data) ? docsResponse.data : [];
              const sorted = [...documentsData].sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
              );
              setRecentDocuments(sorted.slice(0, 3));
            }
          }
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Erro ao carregar dados do servidor.');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [user, isLoading]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      {/* Calculate derived appointment stats */}
      {(() => {
        const todayAppointmentsCount =
          isDoctorRole(user.role)
            ? doctorAppointments.filter((apt) => isAppointmentToday(apt)).length
            : 0;
        const upcomingAppointments =
          isDoctorRole(user.role)
            ? getUpcomingAppointments(doctorAppointments)
            : [];

        return (
          <div className="flex-1 flex flex-col">
        {isAdminRole(user.role) ? (
          <DashboardHeader userName={user.name} />
        ) : isDoctorRole(user.role) ? (
          <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Olá, {user.name.split(' ')[0]}.
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Você tem {todayAppointmentsCount} consultas agendadas para hoje.
                </p>
              </div>
              <div className="flex items-center gap-4">
                {doctorProfile && (
                  <AvailabilityToggle 
                    userId={user.id} 
                    initialAvailable={doctorProfile.isAvailable || false}
                    isApproved={doctorProfile.isApproved}
                    approvalStatus={doctorProfile.status}
                    initialModes={doctorProfile.onCallModes}
                  />
                )}
              </div>
            </div>
          </header>
        ) : (
          <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Início</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Bem-vindo de volta, {user.name}!</p>
          </header>
        )}

        <main className="flex-1 p-8 space-y-8 bg-gray-50 dark:bg-neutral-950 overflow-auto">
          {error && (
            <div className="bg-amber-50 p-4 rounded-lg flex gap-3 text-amber-800">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          {isAdminRole(user.role) && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <DashboardStatCard label="DOCTORS" value={stats.doctors || 0} icon={Users} color="teal" />
                <DashboardStatCard label="PATIENTS" value={stats.patients || 0} icon={Users} color="blue" />
                <DashboardStatCard label="PENDING" value={stats.pendingApprovals || 0} icon={AlertCircle} color="rose" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <ConsultationGrowthChart 
                    data={adminAppointmentsEvolution} 
                    isLoading={isLoadingData} 
                  />
                </div>
                <SystemAlertsPanel 
                  alerts={adminAlerts} 
                  isLoading={isLoadingData} 
                />
              </div>
              <RecentApplicationsTable 
                applications={adminPendingDoctors} 
                isLoading={isLoadingData} 
              />
            </div>
          )}

          {isDoctorRole(user.role) && (
            <>
              <OnDemandMatchListener userId={user.id} />
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <DashboardStatCard label="Hoje" value={todayAppointmentsCount} icon={Stethoscope} color="teal" />
                  {(() => {
                    const ratingDisplay = formatDoctorSatisfactionDisplay(
                      stats.satisfactionRating,
                      stats.ratingsCount,
                    );
                    const count = stats.ratingsCount ?? 0;
                    return (
                      <DashboardStatCard
                        label="Avaliação"
                        value={ratingDisplay.value}
                        icon={Heart}
                        color="rose"
                        subtitle={
                          ratingDisplay.hasRatings
                            ? count === 1
                              ? '1 avaliação'
                              : `${count} avaliações`
                            : 'Aguardando primeiras avaliações'
                        }
                      />
                    );
                  })()}
                </div>
                
                {doctorProfile && doctorProfile.status === 'REJECTED' && (
                  <DoctorRejectionNotice />
                )}

                {doctorProfile && !doctorProfile.isApproved && doctorProfile.status !== 'REJECTED' && (
                  <MedicalCredentialsUpload
                    doctorId={doctorProfile.id}
                    onAllDocumentsUploaded={() => {
                      if (user) {
                        getDoctorByUserId(user.id).then((response) => {
                          if (response.success && response.data) {
                            setDoctorProfile(response.data);
                          }
                        });
                      }
                    }}
                  />
                )}

                {doctorProfile && doctorProfile.isApproved && doctorProfile.professionalDocumentsUrl && (
                  <DoctorDocumentsUpload
                    doctorId={doctorProfile.id}
                    hasDocuments={!!doctorProfile.professionalDocumentsUrl}
                    documentUrl={doctorProfile.professionalDocumentsUrl}
                  />
                )}

                <NextAppointmentsTable
                  appointments={upcomingAppointments}
                  isLoading={isLoadingData}
                  onViewAll={() => navigate('/agenda')}
                />
                
                <section className="bg-white dark:bg-neutral-900 rounded-xl p-6 border border-gray-200 dark:border-neutral-800">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-teal-600">
                        <Users className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Meus Pacientes</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Pacientes com vínculo permanente estabelecido</p>
                      </div>
                    </div>
                  </div>
                  <ConnectionsList role="DOCTOR" />
                </section>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2"><ActivityTimeline /></div>
                  <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Resumo do Mês</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Pacientes Ativos</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{stats.patientsCount || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {isPatientRole(user.role) && (
            <div className="space-y-6">
              <div className="bg-linear-to-r from-teal-600 to-teal-800 dark:from-teal-800 dark:to-teal-950 rounded-2xl p-8 text-white shadow-lg shadow-teal-500/20 relative overflow-hidden">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="max-w-md">
                    <h2 className="text-3xl font-bold mb-2">Precisa de atendimento agora?</h2>
                    <p className="text-teal-50/80 text-sm">
                      Conecte-se com um médico de plantão em poucos minutos para consultas de baixa complexidade.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/on-demand/triage')}
                    className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 group whitespace-nowrap"
                  >
                    <Stethoscope className="w-6 h-6" />
                    Falar com um médico agora
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PatientNextAppointmentCard userId={user.id} />
                <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800 flex flex-col h-full">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Documentos Recentes</h3>
                  <div className="flex-1 space-y-3">
                    {isLoadingData ? (
                      <div className="flex items-center justify-center py-6 text-teal-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                    ) : recentDocuments.length > 0 ? (
                      recentDocuments.map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() =>
                            navigate(`/meus-documentos?documentId=${encodeURIComponent(doc.id)}`)
                          }
                          className="w-full text-left p-3 rounded-lg border border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors group flex items-center justify-between"
                        >
                          <div className="min-w-0 pr-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {patientDocumentDisplayName(doc)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(doc.createdAt).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 group-hover:text-teal-600" />
                        </button>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        não há documentos prescritos
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <section className="bg-white dark:bg-neutral-900 rounded-xl p-6 border border-gray-200 dark:border-neutral-800">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-teal-600">
                      <Stethoscope className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">Meus Médicos</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Seu corpo clínico de confiança</p>
                    </div>
                  </div>
                </div>
                <ConnectionsList role="PATIENT" />
              </section>
            </div>
          )}
        </main>
        </div>
        );
      })()}
    </div>
  );
}
